import { cap } from '../../../State'
import { translations } from '../../../translations'
const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

export const getProcessState = (conv) => {
  const value = conv?.processes?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0]
    ?.state
  if (conv.status === 'paused') return { label: t.inAttesaDiConferma, value }
  if (conv.status === 'canceled') return { label: t.cancellata, value }
  if (conv.status === 'ignored') return { label: t.ignorata, value }
  if (value === 3 && conv.status === 'ended') {
    if (conv.numeroTrasferta) return { label: t.trasfertaCreata, value }
    return { label: t.terminato, value }
  }
  if (value === 3 && conv.status === 'error') {
    if (!conv.noraIsIncluded) return { label: t.noraEsclusa, value } // Label di esclusione di Nora
    return { label: jsonErrors?.[conv.errorCode] || t.gestireDaMidoffice, value }
  }
  return { label: STATE_ENUM?.find((e) => e.value === value)?.label || t.inEsecuzione, value }
}

export const STATE_ENUM = [
  { label: t.creato, value: 1, code: 'INITIAL', color: 'blue' },
  { label: t.inEsecuzione, value: 2, code: 'RUNNING', color: 'violet' },
  { label: t.terminato, value: 3, code: 'ENDED', color: 'green' },
  { label: t.errore, value: 5, code: 'ERROR', color: 'red' },
  { label: t.prontoARipartire, value: 7, code: 'READYTORESTART', color: 'orange' },
  { label: t.cancellata, value: 8, code: 'CANCELLED', color: 'yellow' }
]

/**
 * Opzioni stato visibili all'utente nella FilterBar, derivate dalla logica di getProcessState.
 * Ogni opzione corrisponde a uno stato "user-facing" (non allo stato DB).
 */
export const USER_STATE_OPTIONS = [
  { label: t.inAttesaDiConferma, code: 'IN_ATTESA' },
  { label: t.trasfertaCreata, code: 'TRASFERTA' },
  { label: t.cancellata, code: 'CANCELLATA' },
  { label: t.ignorata, code: 'IGNORATA' },
  { label: t.noraEsclusa, code: 'NORA_ESCLUSA' },
  { label: t.gestireDaMidoffice, code: 'GESTIRE_MIDOFFICE' },
  { label: t.inEsecuzione, code: 'IN_ESECUZIONE' },
  { label: t.terminato, code: 'TERMINATO' }
]

export const getStateValues = () => {
  const obj = {}
  STATE_ENUM.forEach((e) => {
    obj[e.code] = e.value
  })
  return obj
}

export function getLabelDescriptionError(error) {
  return jsonErrors?.[error] || jsonErrors.default
}

const jsonErrors = {
  passeggeriMultipli: t.passeggeriMultipli,
  attachmentsInEmail: t.attachmentsInEmail,
  nessunProfiloTrovato: t.nessunProfiloTrovato,
  noProfileInfoTrovato: t.noProfileInfoTrovato,
  nessunProfiloTrovatoTA: t.nessunProfiloTrovatoTA,
  profiloMittenteNonTA: t.profiloMittenteNonTA,
  duplicazioneProfiloTrovato: t.duplicazioneProfiloTrovato,
  richiestaModificaOCancellazioneTrasferta: t.richiestaModificaOCancellazioneTrasferta,
  richiestaAggiornamentoTrasferta: t.richiestaAggiornamentoTrasferta,
  outOfOffice: t.outOfOffice,
  sollecito: t.sollecito,
  maxAIMessagesReachedInGC: t.maxAIMessagesReachedInGC,
  default: t.gestireDaMidoffice
}

/**
 * Mappa dei colori CSS per ogni stato della conversazione.
 * Restituisce un colore hex basato sul codice colore dello stato.
 * @param {string} colorCode - Codice colore dallo STATE_ENUM (es. 'blue', 'green', 'red')
 * @returns {string} Colore CSS hex corrispondente allo stato
 */
const STATE_COLOR_MAP = {
  blue: '#2563eb',
  violet: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
  orange: '#f59e0b',
  yellow: '#eab308'
}

/**
 * Restituisce il colore CSS associato a un determinato valore di stato.
 * @param {number} stateValue - Valore numerico dello stato dalla conversazione
 * @returns {string} Colore CSS hex. Ritorna grigio (#6b7280) se lo stato non è trovato
 */
export const getStateColor = (stateValue) => {
  const stateEntry = STATE_ENUM.find((e) => e.value === stateValue)
  return stateEntry ? STATE_COLOR_MAP[stateEntry.color] || '#6b7280' : '#6b7280'
}

/** Colore neutro per stati conclusi (canceled, ended senza trasferta, error gestireDaMidoffice) */
const NEUTRAL_COLOR = '#6b7280'

/** Colore verde per trasferta creata (ended con numeroTrasferta) */
const GREEN_COLOR = '#10b981'

/** Colore viola per stato ignored */
const VIOLET_COLOR = '#8b5cf6'

/** Colore rosso per nora esclusa (error con value 3 e !noraIsIncluded) */
const RED_COLOR = '#ef4444'

/** Colore giallo per stato in attesa di conferma (paused) */
const WARNING_COLOR = '#f59e0b'

/**
 * Restituisce il colore di visualizzazione per la badge stato in base alla conversazione.
 * - Verde: ended con numeroTrasferta (trasferta creata)
 * - Neutro: canceled, ended senza trasferta, error con value 3 e label gestireDaMidoffice (noraIsIncluded true)
 * - Viola: ignored
 * - Giallo: paused (in attesa di conferma)
 * - Altri: da getStateColor(value)
 * @param {Object} conv - Oggetto conversazione (o riga con status, processState, noraIsIncluded, numeroTrasferta)
 * @returns {string} Colore CSS hex
 */
export const getStateDisplayColor = (conv) => {
  if (!conv) return NEUTRAL_COLOR
  const status = conv.status
  const value =
    conv.processState?.value ??
    conv?.processes?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0]?.state

  if (status === 'paused') return WARNING_COLOR
  if (status === 'canceled') return NEUTRAL_COLOR
  if (status === 'ended' && conv.numeroTrasferta) return GREEN_COLOR
  if (status === 'ended') return NEUTRAL_COLOR
  if (status === 'ignored') return VIOLET_COLOR
  if (status === 'error' && value === 3 && !conv.noraIsIncluded) return RED_COLOR
  if (status === 'error' && value === 3 && conv.noraIsIncluded) return NEUTRAL_COLOR

  return getStateColor(value)
}

/**
 * Mappa delle icone PrimeIcons per ogni codice stato della conversazione.
 */
const STATE_ICON_MAP = {
  INITIAL: 'pi pi-circle',
  RUNNING: 'pi pi-spin pi-spinner',
  ENDED: 'pi pi-check-circle',
  ERROR: 'pi pi-times-circle',
  READYTORESTART: 'pi pi-refresh',
  CANCELLED: 'pi pi-ban'
}

/**
 * Restituisce la classe icona PrimeIcons associata a un determinato valore di stato.
 * @param {number} stateValue - Valore numerico dello stato dalla conversazione
 * @returns {string} Classe icona PrimeIcons. Ritorna 'pi pi-circle' se lo stato non è trovato
 */
export const getStateIcon = (stateValue) => {
  const stateEntry = STATE_ENUM.find((e) => e.value === stateValue)
  return stateEntry ? STATE_ICON_MAP[stateEntry.code] || 'pi pi-circle' : 'pi pi-circle'
}

/**
 * Mappa delle icone per ogni canale di comunicazione.
 */
const CHANNEL_ICON_MAP = {
  email: 'pi pi-envelope',
  whatsapp: 'pi pi-whatsapp',
  midoffice: 'pi pi-desktop',
  debug: 'pi pi-code'
}

/**
 * Restituisce la classe icona PrimeIcons per un canale di comunicazione.
 * @param {string} channel - Nome del canale di comunicazione (es. 'email', 'whatsapp')
 * @returns {string} Classe icona PrimeIcons. Ritorna 'pi pi-comment' come default
 */
export const getChannelIcon = (channel) => {
  return CHANNEL_ICON_MAP[channel] || 'pi pi-comment'
}

/**
 * Formatta le informazioni del viaggiatore in un oggetto strutturato
 * per una visualizzazione più leggibile nella tabella.
 * @param {Object} traveller - Oggetto viaggiatore dalla conversazione
 * @param {string} [traveller.name] - Nome del viaggiatore
 * @param {string} [traveller.surname] - Cognome del viaggiatore
 * @param {string} [traveller.email] - Email del viaggiatore
 * @param {string} [traveller.phone] - Telefono del viaggiatore
 * @param {string} [traveller.pid] - PID del viaggiatore
 * @returns {Object} Oggetto con fullName, email, phone, pid
 */
export const formatTravellerInfo = (traveller) => {
  if (!traveller) return { fullName: '-', email: '', phone: '', pid: '' }

  const fullName = [traveller.name, traveller.surname].filter(Boolean).join(' ') || '-'
  return {
    fullName,
    email: traveller.email || '',
    phone: traveller.phone || '',
    pid: traveller.pid || ''
  }
}
